import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { deleteUser } from "../../actions/deleteUser";
import { getUsers } from "../../actions/getUsers";
import { lang } from "../../lang";
import { browserState } from "../../store/browserState";
import { Button } from "../../components/atoms/Button";
import { CenteredContent } from "../../components/atoms/CenteredContent";
import { FlexHorizontal, FlexSpacer } from "../../components/atoms/Flex";
import { Title } from "../../components/atoms/Text";
import { PopupMenu } from "../../components/molecules/PopupMenu";
import { Table } from "../../components/molecules/Table";
import { CreateUser } from "../../components/organisms/CreateUser";
import { PopupConfirm } from "../../components/molecules/PopupConfirm";
import { Template } from "../../components/organisms/Template";
import { MenuItem, TablePropsData, TableTransformParam, User } from "../../interface";
import { catchEndpoint } from "../../catchEndpoint";
import { ROUTE_TYPE } from "../../HelperSrc";
import { api } from "../../api";
import { store } from "../../store";
import * as HelperSrc from "../../HelperSrc";

@observer
class AdminUsersPageBase extends React.Component<RouteComponentProps> {
    public state = {
        resetLinkPopup: null as unknown as string,
        addPopup: false,
        deletePopup: false,
        editPopup: null as unknown as User,
        userMenu: null as unknown as User,
        x: 0,
        y: 0,
        userAccounts: [] as unknown as User[],
        usersLoading: false,
        userId: undefined
    };
    public componentDidMount() {
        this.fetchUsers();
    }

    public fetchUsers() {
        this.setState({ usersLoading: true });
        getUsers()
            .then((users) => {
                this.setState({ userAccounts: users, usersLoading: false });
            })
            .catch(catchEndpoint);
    }

    public openUserMenu = (row: TablePropsData) => {
        this.setState({
            userMenu: row,
            x: browserState.width - browserState.mouseX,
            y: browserState.mouseY
        });
    };

    public handleClickPopupItem = (menuitem: MenuItem) => {
        const user = this.state.userMenu;
        this.setState({ userMenu: null });
        if (menuitem.key === "permission") {
            this.setState({
                editPopup: user
            });
        } else if (menuitem.key === "reset") {
            this.props.history.push(ROUTE_TYPE.PROFILE, { userId: user.id });
        } else if (menuitem.key === "delete") {
            this.setState({
                deletePopup: true,
                userId: user.id
            });
        } else if (menuitem.key === "opt_ad") {
            if (api.userApi) {
                api.userApi
                    .optApi(user.id)
                    .then((response) => {
                        let message = "";
                        let isSuccess = false;

                        if (response instanceof Error || (response.message && response.message.error)) {
                            if (response instanceof Error) {
                                HelperSrc.writeLog("error", response);
                            } else {
                                message = lang[response.message.error];
                            }
                        } else {
                            message = lang[response.message.success];
                            isSuccess = true;
                        }

                        store.doNotice(message, isSuccess);
                    })
                    .catch(catchEndpoint);
            }
        }
    };

    public render() {
        const { addPopup, deletePopup, editPopup, userMenu, userAccounts, userId } = this.state;
        const userList: (User | undefined)[] = [];
        const columnNames = [
            {
                id: "username",
                name: lang.userName,
                transform: (username: TableTransformParam) => {
                    const user = userAccounts.find((item) => item.username === username);

                    userList.push(user);

                    return username;
                }
            },
            {
                id: "role",
                name: "Role",
                transform: () => {
                    const user = userList[userList.length - 1];

                    if (user) {
                        if (user.is_superuser) {
                            return lang.isSuperuser;
                        } else if (user.is_staff) {
                            return lang.isStaff;
                        } else if (user.is_support) {
                            return lang.isSupport;
                        }
                    }
                }
            },
            {
                id: "groupnames",
                name: lang.permissions,
                transform: (id: TableTransformParam) => (id as string[]).join(", ")
            }
        ];

        let itemList = [
            { key: "permission", name: lang.editPermission },
            { key: "reset", name: lang.resetPsw },
            { key: "delete", name: lang.delete, color: "item_delete" },
            { key: "opt_ad", name: lang.optAD }
        ];

        if (store.user.is_support) {
            itemList = itemList.slice(-1);
        }

        return (
            <>
                <Template>
                    <CenteredContent>
                        <FlexHorizontal>
                            <Title>{lang.userManagement}</Title>
                            <FlexSpacer />
                            <div className="container_action">
                                {!store.user.is_support && (
                                    <Button
                                        id="createuserbutton"
                                        data-at_id="open_user_popup_button"
                                        title={lang.add}
                                        style={{ marginRight: "-10px" }}
                                        onClick={() => {
                                            this.setState({
                                                addPopup: true,
                                                editPopup: null
                                            });
                                        }}
                                    />
                                )}
                            </div>
                        </FlexHorizontal>
                        <Table
                            id="user-table"
                            loading={this.state.usersLoading}
                            onClickRow={this.openUserMenu}
                            pageSize={5}
                            columnNames={columnNames}
                            data={userAccounts}
                        />
                    </CenteredContent>
                </Template>
                {(addPopup || editPopup) && (
                    <CreateUser
                        edit={editPopup}
                        onClose={(ok: boolean | undefined) => {
                            this.setState({ addPopup: false, editPopup: null });
                            if (ok) {
                                this.fetchUsers();
                            }
                        }}
                    />
                )}
                {deletePopup && (
                    <PopupConfirm
                        onClose={() => {
                            this.setState({ deletePopup: false });
                        }}
                        onClick={() => {
                            this.setState({
                                deletePopup: false,
                                usersLoading: true
                            });
                            deleteUser(userId)
                                .catch(catchEndpoint)
                                .finally(() => {
                                    this.fetchUsers();
                                });
                        }}
                        title={lang.deleteUserMessage}
                    />
                )}
                {userMenu && (
                    <PopupMenu
                        id="popup"
                        title={userMenu.username}
                        items={itemList}
                        onClick={this.handleClickPopupItem}
                        onClose={() => {
                            this.setState({ userMenu: null });
                        }}
                    />
                )}
            </>
        );
    }
}

const AdminUsersPage = withRouter(AdminUsersPageBase);

export { AdminUsersPage };
